import { supabase } from '@/lib/supabase'

/**
 * CSV export helpers — real pediatric-visit workflow. Docs need a plain feed log,
 * growth chart values, and diaper history. Client-side conversion so we don't need
 * a server round-trip for what's already in IndexedDB / the visible query.
 *
 * All exports scoped by family_id + optional person_id via the caller's RLS-checked
 * session — the same rules that gate the UI gate the CSV.
 */

type CsvRow = Record<string, string | number | null | undefined>

function csvCell(v: unknown): string {
  if (v == null) return ''
  const s = typeof v === 'string' ? v : JSON.stringify(v)
  // Quote if it contains a delimiter, quote, or newline. Escape embedded quotes.
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function toCsv(rows: CsvRow[], columns: string[]): string {
  const header = columns.map(csvCell).join(',')
  const body = rows.map((r) => columns.map((c) => csvCell(r[c])).join(',')).join('\n')
  return `${header}\n${body}`
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/** Feeds — every early_care.feeding.{recorded,corrected} event in the window. */
export async function exportFeedsCsv(familyId: string, personId: string | null, days: number): Promise<void> {
  const since = new Date(Date.now() - days * 86400000).toISOString()
  let q = supabase
    .from('events')
    .select('id, occurred_at, event_type, payload, user_id, supersedes_event_id')
    .eq('family_id', familyId)
    .in('event_type', ['early_care.feeding.recorded', 'early_care.feeding.corrected'])
    .gte('occurred_at', since)
    .order('occurred_at', { ascending: true })
  if (personId) q = q.eq('person_id', personId)
  const { data, error } = await q
  if (error) throw new Error(error.message)

  const supersededIds = new Set((data ?? []).map((r) => r.supersedes_event_id).filter(Boolean))
  const rows: CsvRow[] = (data ?? [])
    .filter((r) => !supersededIds.has(r.id))
    .map((r) => {
      const p = (r.payload ?? {}) as Record<string, unknown>
      return {
        occurred_at: r.occurred_at,
        feed_type: (p.feed_type as string) ?? '',
        volume_ml: (p.volume_ml as number) ?? '',
        duration_minutes: (p.duration_minutes as number) ?? '',
        note: (p.note as string) ?? '',
        corrected: r.event_type.endsWith('.corrected') ? 'yes' : '',
      }
    })

  downloadCsv(`babyos-feeds-last-${days}d-${dateStamp()}.csv`, toCsv(rows, ['occurred_at', 'feed_type', 'volume_ml', 'duration_minutes', 'note', 'corrected']))
}

/** Diapers — every early_care.diaper.{recorded,corrected} event in the window. */
export async function exportDiapersCsv(familyId: string, personId: string | null, days: number): Promise<void> {
  const since = new Date(Date.now() - days * 86400000).toISOString()
  let q = supabase
    .from('events')
    .select('id, occurred_at, event_type, payload, supersedes_event_id')
    .eq('family_id', familyId)
    .in('event_type', ['early_care.diaper.recorded', 'early_care.diaper.corrected'])
    .gte('occurred_at', since)
    .order('occurred_at', { ascending: true })
  if (personId) q = q.eq('person_id', personId)
  const { data, error } = await q
  if (error) throw new Error(error.message)

  const supersededIds = new Set((data ?? []).map((r) => r.supersedes_event_id).filter(Boolean))
  const rows: CsvRow[] = (data ?? [])
    .filter((r) => !supersededIds.has(r.id))
    .map((r) => {
      const p = (r.payload ?? {}) as Record<string, unknown>
      return {
        occurred_at: r.occurred_at,
        diaper_type: (p.diaper_type as string) ?? '',
        consistency: (p.consistency as string) ?? '',
        color: (p.color as string) ?? '',
        note: (p.note as string) ?? '',
        corrected: r.event_type.endsWith('.corrected') ? 'yes' : '',
      }
    })

  downloadCsv(`babyos-diapers-last-${days}d-${dateStamp()}.csv`, toCsv(rows, ['occurred_at', 'diaper_type', 'consistency', 'color', 'note', 'corrected']))
}

/** Growth — every measurement row in the window, from the measurements table. */
export async function exportGrowthCsv(familyId: string, personId: string | null, days: number): Promise<void> {
  const since = new Date(Date.now() - days * 86400000).toISOString()
  let q = supabase
    .from('measurements')
    .select('measured_at, measurement_type, value, unit, label, source, source_ref')
    .eq('family_id', familyId)
    .gte('measured_at', since)
    .order('measured_at', { ascending: true })
  if (personId) q = q.eq('person_id', personId)
  const { data, error } = await q
  if (error) throw new Error(error.message)

  const rows: CsvRow[] = (data ?? []).map((r) => ({
    measured_at: r.measured_at as string,
    measurement_type: r.measurement_type as string,
    value: r.value as number,
    unit: r.unit as string,
    label: (r.label as string | null) ?? '',
    source: (r.source as string | null) ?? '',
  }))

  downloadCsv(`babyos-growth-last-${days}d-${dateStamp()}.csv`, toCsv(rows, ['measured_at', 'measurement_type', 'value', 'unit', 'label', 'source']))
}

/** Sleep — start/end pairs, computed as durations. */
export async function exportSleepCsv(familyId: string, personId: string | null, days: number): Promise<void> {
  const since = new Date(Date.now() - days * 86400000).toISOString()
  let q = supabase
    .from('events')
    .select('id, occurred_at, event_type, payload, supersedes_event_id')
    .eq('family_id', familyId)
    .in('event_type', ['early_care.sleep.started', 'early_care.sleep.ended'])
    .gte('occurred_at', since)
    .order('occurred_at', { ascending: true })
  if (personId) q = q.eq('person_id', personId)
  const { data, error } = await q
  if (error) throw new Error(error.message)

  const supersededIds = new Set((data ?? []).map((r) => r.supersedes_event_id).filter(Boolean))
  const active = (data ?? []).filter((r) => !supersededIds.has(r.id))

  // Pair starts with ends. If a start has no end (still sleeping), leave end blank.
  const rows: CsvRow[] = []
  let currentStart: { at: string; location: string } | null = null
  for (const r of active) {
    const p = (r.payload ?? {}) as Record<string, unknown>
    if (r.event_type === 'early_care.sleep.started') {
      if (currentStart) {
        // Orphan start — previous start never got an end. Record it with no end.
        rows.push({
          start: currentStart.at,
          end: '',
          duration_minutes: '',
          location: currentStart.location,
        })
      }
      currentStart = { at: r.occurred_at as string, location: (p.location as string) ?? '' }
    } else if (r.event_type === 'early_care.sleep.ended' && currentStart) {
      const startMs = new Date(currentStart.at).getTime()
      const endMs = new Date(r.occurred_at as string).getTime()
      const mins = Math.round((endMs - startMs) / 60000)
      rows.push({
        start: currentStart.at,
        end: r.occurred_at as string,
        duration_minutes: mins,
        location: currentStart.location,
      })
      currentStart = null
    }
  }
  // Trailing start (baby still sleeping)
  if (currentStart) {
    rows.push({ start: currentStart.at, end: '', duration_minutes: '', location: currentStart.location })
  }

  downloadCsv(`babyos-sleep-last-${days}d-${dateStamp()}.csv`, toCsv(rows, ['start', 'end', 'duration_minutes', 'location']))
}

function dateStamp(): string {
  return new Date().toISOString().slice(0, 10)
}
